import Translator, { languageFromPreference } from '../locales/translator';

/**
 * DataStore utility class for managing the application's data store.
 * Provides methods for initializing, updating, and clearing user data.
 * Follows the same pattern as other utility classes in the codebase (Utils, AccessControl).
 */
export default class DataStore {
    /**
     * Creates the initial data store structure with all properties initialized.
     * This ensures MobX tracks all properties from the start.
     * 
     * @param {string} lang - Language code (e.g., 'en', 'fr')
     * @returns {Object} Initial data store object
     */
    static createInitialDataStore(lang) {
        return {
            username: "",
            hideMenu: false,
            isMobile: false,
            isMenuOpen: false,
            t: new Translator(lang),
            selectedStatus: "All",
            // Initialize properties that will be set dynamically to ensure MobX tracks them
            role: null,
            roles_config: null,
            expiration_date: null,
            firstName: null,
            lastName: null,
            origin_role: null,
            unit: null,
            password_expired: null,
            config: null,
            client_name: null,
            id_client: null,
            id_user: null,
            vacation_mode: null,
            home_office_id: null,
            // XPEN-3187: cached once per session from GET /xpens-categories/,
            // shared by every screen that renders a category label — never
            // per-component useState (Non-Negotiable: shared state in MobX).
            xpensCategories: null
        };
    }

    /**
     * Sets the selected status filter in the data store.
     * 
     * @param {Object} data - The MobX observable data store
     * @param {string} status - The status to set (e.g., "All", "Pending", "Completed")
     */
    static setSelectedStatus(data, status) {
        data.selectedStatus = status;
    }

    /**
     * Caches the standard XPENS category list on the data store, fetched
     * once per session from GET /xpens-categories/ (XPEN-3187).
     *
     * @param {Object} data - The MobX observable data store
     * @param {Array} categories - The category documents from the API
     */
    static setXpensCategories(data, categories) {
        data.xpensCategories = categories;
    }

    /**
     * Switches the interface to the firm's language preference unless the person picked a language.
     * The translator is replaced, as the menu does, so observers re-render in the new language.
     *
     * @param {Object} data - The MobX observable data store
     */
    static applyFirmLanguage(data) {
        const lang = languageFromPreference(data.config?.language_preference);
        if (!lang || Translator.userChoseLang() || data.t?.getCurrentLang() === lang) return;
        data.t.changeLang(lang);
        data.t = new Translator(lang);
    }

    /**
     * Updates the data store with user authentication data from an API response.
     * This centralizes the mapping between API response fields and data store properties.
     * 
     * @param {Object} data - The MobX observable data store
     * @param {Object} responseData - The response data from the API
     * @param {string} token - The JWT token to store
     */
    static updateUserData(data, responseData, token) {
        // Store token in localStorage
        if (token) {
            window.localStorage.setItem("jwt", token);
        }

        // Map API response fields to data store properties
        if (responseData.username !== undefined) data.username = responseData.username;
        if (responseData.first_name !== undefined) data.firstName = responseData.first_name;
        if (responseData.last_name !== undefined) data.lastName = responseData.last_name;
        if (responseData.origin_role !== undefined) data.origin_role = responseData.origin_role;
        if (responseData.unit !== undefined) data.unit = responseData.unit;
        if (responseData.role !== undefined) data.role = responseData.role;
        if (responseData.password_expired !== undefined) data.password_expired = responseData.password_expired;
        if (responseData.config !== undefined) {
            data.config = responseData.config;
            // XPENS staff connect as one firm after another; only the firm's own people follow its language.
            if ((responseData.origin_role || responseData.role) !== "SuperAdmin") DataStore.applyFirmLanguage(data);
        }
        if (responseData.client_name !== undefined) data.client_name = responseData.client_name;
        if (responseData.id_client !== undefined) data.id_client = responseData.id_client;
        if (responseData.expiration_date !== undefined) data.expiration_date = responseData.expiration_date;
        if (responseData.roles_config !== undefined) data.roles_config = responseData.roles_config;
        if (responseData.id_user !== undefined) data.id_user = responseData.id_user;
        if (responseData.vacation_mode !== undefined) data.vacation_mode = responseData.vacation_mode;
        if (responseData.home_office_id !== undefined) data.home_office_id = responseData.home_office_id;
    }

    /**
     * Clears user authentication data from the data store.
     * This is useful for logout or when authentication fails.
     * 
     * @param {Object} data - The MobX observable data store
     */
    static clearUserData(data) {
        data.username = "";
        data.role = null;
        data.roles_config = null;
        data.expiration_date = null;
        data.firstName = null;
        data.lastName = null;
        data.origin_role = null;
        data.unit = null;
        data.password_expired = null;
        data.config = null;
        data.client_name = null;
        data.id_client = null;
        data.id_user = null;
        data.vacation_mode = null;
        data.home_office_id = null;
        window.localStorage.removeItem("jwt");
    }
}
